import { useCallback, useEffect, useId, useRef, useState } from "react";
import { EventPeriodField } from "./EventPeriodField";
import { ARCHIVE_REFUSED, archiveTabUrl, canArchive } from "./archiveRules";
import {
  Archive,
  ArchiveRestore,
  ArrowRight,
  Download,
  Printer,
  History,
  LayoutTemplate,
  Lock,
  Plus,
  Receipt,
  Save,
  Send,
  Workflow,
} from "lucide-react";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { QuietField, quietControlClass } from "../../components/QuietField";
import { SearchPicker, normalize } from "../../components/SearchPicker";
import type { PickerAction, PickerOption } from "../../components/SearchPicker";
import { CompanyDialog } from "../companies/CompanyDialog";
import { useToast } from "../../components/Toast";
import { SendDialog } from "../send/SendDialog";
import { ApplyTemplateDialog } from "../templates/ApplyTemplateDialog";
import { RevisionPanel } from "../revisions/RevisionPanel";
import { QuoteIdContext } from "./attachmentMention";
import { AttachmentPanel } from "./AttachmentPanel";
import { ProjectCost } from "../expenses/ProjectCost";
import { QuoteMeetingNotes } from "../meetings/QuoteMeetingNotes";
import { SignoffInvite } from "./SignoffInvite";
import { InvoiceRequestDialog } from "./InvoiceRequestDialog";
import { OwnerPicker } from "./OwnerPicker";
import { PartyBlock, PartyReadOnlyField } from "./PartyBlock";
import { PaymentTerms } from "./PaymentTerms";
import { SectionCard } from "./SectionCard";
import { TotalsBar } from "./TotalsBar";
import { taxRateError, toQuoteUpdate, useQuoteDraft } from "./useQuoteDraft";
import { percentToRate, rateToPercent } from "../../lib/money";
import { formatTaipeiDateTime } from "../../lib/datetime";
import { useAuth } from "../auth/useAuth";
import { api, ConflictError, loadQuoteStatuses } from "../../api/client";
import { ExportDialog } from "./ExportDialog";
import { MoneyStrip } from "./MoneyStrip";
import { Receipts } from "./Receipts";
import { QUOTE_IS_READ_ONLY_FOR_ROLE, TAX_MODE_LABEL, canWriteQuotes } from "../../api/types";
import type {
  CatalogHit,
  Company,
  CompanyContact,
  QuoteDetail,
  QuoteLine,
  QuotePaymentTerm,
  QuoteStatus,
  QuoteStatusEvent,
  StatusOption,
  TaxMode,
  TemplateSummary,
  UserPick,
} from "../../api/types";
import type { SectionNavItem } from "../../layout/SectionNav";
import type { CommandLineTarget } from "../command/CommandPalette";

// 報價單編輯器。章節與明細自由編輯,右下角即時總計隨打隨算。
//
// 三條規矩寫死在這裡:
// 1. 存檔後以後端回傳的內容整份取代草稿 —— 金額一律由後端重算,前端算的只是預覽。
// 2. 版本衝突(409)絕不覆蓋,跳對話框讓人自己決定要不要重新載入。
// 3. 已封存的單是唯讀的(規格 §4.1)。編輯器照樣打得開、內容照樣讀得到,
//    但存檔鈕是 disabled,頂上掛一條**寫著字**的橫幅 —— 誰在什麼時候封的、
//    要編輯請先復原。能存檔就會有人把改動存進一張所有人都看不到的單裡,
//    還會連帶發出「某某改了你負責的單」的通知,而收到通知的人在列表上
//    根本找不到那張單。
// 4. 抬頭分成甲方與乙方兩塊。甲方只有「專案承辦人」可選,乙方只有「客戶」可選
//    加上三個聯絡欄可填;其餘都是推導值,顯示成唯讀並標上 aria-disabled。
//    要改就去它們真正的來源改(公司設定 / 使用者管理 / 客戶檔),
//    在報價單上多開一個手填欄位,同一份資料馬上會有兩種說法。
// 5. 財務對報價單唯讀(設計規格 §2.1)。與第 3 條同一種處置 —— **這一頁對他
//    不是消失,是改不動**:他看得到內容與金額(看不到金額的財務,連發票對不
//    對得上都查不了),寫入的那幾顆鈕反灰並說得出原因。角色判斷走
//    `canWriteQuotes`,不在這個檔案裡比對角色字串。

const TAX_MODE_OPTIONS = (Object.keys(TAX_MODE_LABEL) as TaxMode[]).map((mode) => ({
  value: mode,
  label: TAX_MODE_LABEL[mode],
}));

/**
 * 法定營業稅率。業主 2026-09-11 定案:「選擇未稅 總額就是小計+小計的5%」。
 * 選未稅時自動帶回來,免得剛剛被含稅歸零的 0 留在欄位裡變成一張 0% 的報價單。
 */
const DEFAULT_TAX_PERCENT = "5";

/** 含稅的稅率固定 0。畫面反灰是體驗,後端另外還會再歸零一次,那才是保證。 */
const ZERO_TAX_PERCENT = "0";

/** 沒有選客戶時,下拉上那一格顯示什麼。 */
const NO_CLIENT = "未指定客戶";

/**
 * 已封存時,會寫進去的那幾顆鈕滑過去看到的那一句。
 *
 * 與後端 `ensure_writable()` 回的 detail 是同一句:按鈕的提示與真的按下去會
 * 收到的錯誤講的必須是同一件事,否則使用者只會覺得兩邊各說各話。
 */
const ARCHIVED_IS_READ_ONLY = "這張報價單已封存,要編輯請先復原";

/**
 * 還沒結案就想申請開發票時,那顆鈕滑過去看到的那一句(設計規格 §3.1)。
 *
 * 「只有已結案的單提得了」是業主流程裡**唯一一個硬性的解鎖條件** ——
 * 後端回的 409 就是這個意思,兩邊講的必須是同一件事。
 */
const INVOICE_NEEDS_CLOSED = "這張單還沒結案,要改成已結案才申請得了開發票";

/** 同一件事在畫面上的短句。反灰不是原因,一顆灰掉的鈕旁邊要有字。 */
const INVOICE_NEEDS_CLOSED_SHORT = "結案後才提得了開票申請";

/**
 * 抬頭上那句「有未存檔的修改」。
 *
 * **付款條件要單獨講得出來**:它走自己的端點,抬頭那顆「儲存」存不了它。
 * 一句籠統的「有未存檔的修改」會讓人按下抬頭的儲存、以為付款條件也一起存了,
 * 而那一區的改動還留在原地。
 */
function unsavedText(body: boolean, paymentTerms: boolean): string {
  if (body && paymentTerms) return "有未存檔的修改(含付款條件)";
  if (paymentTerms) return "付款條件有未存檔的修改";
  return "有未存檔的修改";
}

/** 封存者的名字查不到時(帳號已刪,`archived_by_user_id` 被 SET NULL)顯示這個。 */
const UNKNOWN_ARCHIVER = "已離職的同仁";

/**
 * 狀態歷程上查不到人名時顯示這個(`actor_user_id` 同樣是 ON DELETE SET NULL)。
 *
 * 與封存那一句刻意分開放:兩邊碰巧是同一句話,但它們答的是兩個不同的問題,
 * 綁在一起之後,哪天其中一邊要改措辭,另一邊會跟著被改掉而沒有人發現。
 */
const UNKNOWN_ACTOR = "已離職的同仁";

/**
 * 封存時間寫成「2026/09/13」。
 *
 * 直接切 ISO 字串的前十個字,不經過 `Date` —— `new Date(...).toLocaleDateString()`
 * 會把 UTC 的時間戳挪到瀏覽器的時區,凌晨封的單在畫面上就變成前一天。
 * 與列表的「最後更新」那一欄同一個做法。
 */
function archivedDay(stamp: string): string {
  return stamp.slice(0, 10).replace(/-/g, "/");
}

/**
 * 後端 `QuoteDetail` 多回的兩個唯讀欄位(`schemas.py`)。
 *
 * 改它們的唯一入口是 `/archive` 與 `/restore-from-archive` 兩支端點,`PUT` 送上去
 * 一律被忽略 —— 所以這裡只讀不寫,也不進草稿(`useQuoteDraft` 管的是可編輯的內容)。
 */
export interface QuoteArchiveStamp {
  archived_at?: string | null;
  archived_by_name?: string;
}

/**
 * 兩支電話併成一行時的分隔符號。業主指定的樣子就是這一串:
 * `0988-555-222 | 04-2691-5555#555`。與後端 `models.join_phones` 同一條規則。
 */
const PHONE_SEPARATOR = " | ";

/**
 * 把手機與市話併成報價單上顯示的那一行。
 *
 * 兩支都有就用 ` | ` 隔開;只有一支就只顯示那一支,**不留多餘的分隔線** ——
 * 「 | 04-2691-5555」看起來像前面漏印了一個號碼;兩支都空就是空字串。
 */
export function joinPhones(mobile: string, landline: string): string {
  return [mobile.trim(), landline.trim()].filter((part) => part !== "").join(PHONE_SEPARATOR);
}

/** 選了客戶會自動帶入的四個欄位,與客戶檔上對應的欄位同名。 */
/** 乙方聯絡人那幾格常駐一條底線:客戶反映以為「選了客戶就鎖死」(業主 2026-11-02)。 */
const EDITABLE_HINT = "border-b-[color:var(--field-hover)]";

const AUTOFILL_FIELDS = ["contact_name", "contact_mobile", "contact_phone", "contact_email"] as const;
type AutofillField = (typeof AUTOFILL_FIELDS)[number];

/** 這四欄在畫面上的名字。要問「可不可以蓋掉」時得說得出是哪一格。 */
const AUTOFILL_LABEL: Record<AutofillField, string> = {
  contact_name: "聯絡人",
  contact_mobile: "聯絡手機",
  contact_phone: "聯絡電話",
  contact_email: "EMAIL",
};

export interface QuoteEditorProps {
  quote: QuoteDetail & QuoteArchiveStamp;
  /** 存檔成功後通知外層,例如刷新清單。 */
  onSaved?: (quote: QuoteDetail) => void;
  /** 封存成功之後要去的地方(封存區那一類的網址);沒給就整頁換過去。 */
  onArchived?: (url: string) => void;
  /** 回報本單的報價分類給外框的側邊欄導覽。七個分類上百行,沒有導覽會迷路。 */
  onSectionsChange?: (sections: SectionNavItem[]) => void;
  /**
   * 回報本單明細給外框的指令面板。⌘K 的「本單明細」那一組結果就是靠它;
   * 沒有人回報的話,面板永遠不會列出那一組,搜尋框上寫的「本單明細」就是空頭支票。
   */
  onLinesChange?: (lines: CommandLineTarget[]) => void;
  /** 外部(⌘K 或範本頁)指定要套用的範本;帶進來就直接開對話框並選好它。 */
  requestedTemplateId?: number | null;
  /** 上面那個請求處理完了,請外層把網址上的參數清掉。 */
  onTemplateRequestHandled?: () => void;
  /** 把「套用範本」交給外框的指令面板;離開這一頁時會傳 null 撤回。 */
  onRegisterApplyTemplate?: (handler: ((template: TemplateSummary) => void) | null) => void;
  /** 把「品項庫帶入目前章節」交給外框的指令面板;同樣離開時撤回。 */
  onRegisterPickCatalog?: (handler: ((hit: CatalogHit) => void) | null) => void;
}

export function QuoteEditor({
  quote,
  onSaved,
  onArchived,
  onSectionsChange,
  onLinesChange,
  requestedTemplateId = null,
  onTemplateRequestHandled,
  onRegisterApplyTemplate,
  onRegisterPickCatalog,
}: QuoteEditorProps) {
  const {
    draft,
    dirty,
    totals,
    sectionSubtotals,
    setField,
    setSection,
    addSection,
    removeSection,
    moveSection,
    addLine,
    removeLine,
    moveLine,
    setLine,
    replace,
  } = useQuoteDraft(quote);

  const toast = useToast();
  const { user } = useAuth();
  // 狀態下拉的 label / hint 綁定用。同一頁可能同時掛著兩個編輯器(測試就是這樣),
  // 寫死的 id 會讓 <label htmlFor> 指到別人那一格。
  const statusFieldId = useId();
  const statusHintId = useId();
  // 稅率欄打的是百分比,草稿裡存的仍然是小數(與後端同一個格式)。
  // 文字自己留一份,否則打到一半的「5.」會被換算回來的「5」吃掉小數點,
  // 5.5% 這種稅率根本打不出來。
  const [taxPercent, setTaxPercent] = useState(() => rateToPercent(draft.tax_rate));

  // 稅率被別的地方改掉時(切稅制、存檔後整份取代、復原、套範本)重新同步。
  // 用 functional updater 比對,自己打字造成的那一次不會被自己蓋回去。
  useEffect(() => {
    setTaxPercent((prev) => (percentToRate(prev) === draft.tax_rate ? prev : rateToPercent(draft.tax_rate)));
  }, [draft.tax_rate]);

  // 含稅時稅率固定 0 且不給改。業主原話:「稅制如果免稅 稅率自動變成0唯讀反灰色」。
  const rateLocked = draft.tax_mode === "INCLUSIVE";
  // 唯讀時顯示的一律是 0,不拿使用者上一刻打到一半的文字當畫面 ——
  // 那格已經打不進去了,裡面留著「」或「abc」只會讓人看不懂這張單怎麼了。
  const ratePercent = rateLocked ? ZERO_TAX_PERCENT : taxPercent;
  // 稅率不合法時直接擋住存檔:送出去只會換來一個 422,錯誤訊息還指不出是哪一格。
  // 但唯讀的那一格不驗:使用者沒有任何辦法把錯誤弄掉,擋下去等於整張單存不了,
  // 而且他不會把「存不了」聯想到稅制 —— 含稅的稅率本來就由後端固定成 0。
  const rateError = rateLocked ? null : taxRateError(taxPercent);

  // 乙方的客戶下拉。清單載不到就留空,其餘欄位照常編輯。
  const [companies, setCompanies] = useState<Company[]>([]);
  useEffect(() => {
    let alive = true;
    api
      .get<Company[]>("/companies")
      .then((rows) => {
        if (alive && Array.isArray(rows)) setCompanies(rows);
      })
      .catch(() => {
        /* 沒有客戶清單也要編得下去 */
      });
    return () => {
      alive = false;
    };
  }, []);

  const [saving, setSaving] = useState(false);
  /**
   * 這張單目前的封存狀態。**兩欄一起存一份 state,不放進草稿。**
   *
   * 草稿裡的東西都是「存檔會送回後端的內容」,而封存的兩欄是唯讀的推導值;
   * 混進去只會讓 `toQuoteUpdate` 有機會把它們送上去(送了也被忽略,但那時
   * 就沒有人看得出這裡是唯讀的了)。
   *
   * 只有這一頁的「復原」改得動它,所以初值取自 prop 之後就自己管,不另外同步。
   */
  const [archive, setArchive] = useState({
    at: quote.archived_at ?? null,
    by: quote.archived_by_name ?? "",
  });
  const [unarchiving, setUnarchiving] = useState(false);
  const archived = archive.at !== null;

  /**
   * 這個角色動不了這張單(設計規格 §2.1:財務對報價單唯讀)。
   *
   * **只有在知道你是誰、而且你動不了的時候才切唯讀。** `user` 是 null 只發生在
   * 單獨 render 這個元件的測試裡(真實動線在 `RequireAuth` 之後才掛載)。
   * 角色判斷一律走 `canWriteQuotes`,不在這裡寫 `role === "FINANCE"` ——
   * 那種字串比對散出去之後,第 15 輪加財務頁面時一定會漏掉其中幾處。
   */
  const readOnly = user !== null && !canWriteQuotes(user.role);

  /**
   * 會寫進這張單的動作為什麼按不動。沒有原因就是按得動。
   *
   * 兩種唯讀**同時成立時先講角色**:財務就算把單復原了也還是改不動,
   * 叫他「先復原」只是讓他去做一件做不到、而且做到了也沒用的事。
   */
  const writeBlockedReason = readOnly
    ? QUOTE_IS_READ_ONLY_FOR_ROLE
    : archived
      ? ARCHIVED_IS_READ_ONLY
      : undefined;
  /** 這張單現在寫不得(角色唯讀或已封存)。 */
  const writeBlocked = writeBlockedReason !== undefined;
  const [conflict, setConflict] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  // null = 沒開;"export" / "print" 是同一個對話框的兩種結尾(見 ExportDialog)。
  const [exportMode, setExportMode] = useState<"export" | "print" | null>(null);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [presetTemplateId, setPresetTemplateId] = useState<number | null>(null);
  const [revisionsOpen, setRevisionsOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);

  /**
   * 七個狀態與它們的中文字樣。**一律從 `GET /api/quote-statuses` 拿**
   * (設計規格 §1.4):前端自己維護一份對照表的話,兩份遲早會漂開,而漂開的
   * 症狀是新加的那個值在下拉裡直接顯示成英文的 `IN_PRODUCTION`。
   *
   * 載不到就退成「只有目前這一個選項」—— 編輯器其餘部分照常用得下去,
   * 使用者頂多是這一刻改不了狀態,而不是整張單打不開。
   */
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);
  const [statusEvents, setStatusEvents] = useState<QuoteStatusEvent[]>([]);
  const [statusError, setStatusError] = useState("");
  const [changingStatus, setChangingStatus] = useState(false);

  /**
   * 這張單的付款條件(設計規格 §2)。**初值取自 prop 之後就交給付款條件那一區自己管。**
   *
   * 它不進草稿:草稿裡的東西都是「存檔會送回後端的內容」,而付款條件走的是
   * 自己的端點(`PUT /quotes/{id}/payment-terms`),`PUT /quotes/{id}` 送什麼
   * 上來一律忽略。也**不必跟著整份取代同步** —— 存檔、寄送、套範本、復原、
   * 改狀態沒有任何一條會動到付款期別,唯一改得動它的就是那一區自己。
   */
  const [paymentTerms, setPaymentTerms] = useState<QuotePaymentTerm[]>(() => quote.payment_terms ?? []);
  /**
   * 付款條件那一區有沒有待存的改動。
   *
   * 它不進草稿(見上面那段),所以 `dirty` 看不到它 —— 使用者把三期比例改成
   * 20/30/50 卻沒按「儲存付款條件」,抬頭要是還寫著「沒有未存檔的修改」,
   * 他就放心跳走了。抬頭那句話要涵蓋它,**而且說得出待存的是付款條件** ——
   * 抬頭那顆「儲存」存的是報價單本體,存不了它。
   */
  const [paymentTermsDirty, setPaymentTermsDirty] = useState(false);

  useEffect(() => {
    let alive = true;
    loadQuoteStatuses()
      .then((rows) => {
        if (alive) setStatusOptions(rows);
      })
      .catch(() => {
        /* 沒有字樣表也要編得下去 */
      });
    return () => {
      alive = false;
    };
  }, []);

  /** 狀態歷程,最新在前。開著對話框才撈 —— 每打開一張單就先白撈一支不划算。 */
  const loadStatusEvents = useCallback(async () => {
    try {
      const rows = await api.get<QuoteStatusEvent[]>(`/quotes/${quote.id}/status-events`);
      setStatusEvents(Array.isArray(rows) ? rows : []);
    } catch {
      // 歷程撈不到不影響改狀態本身,清空就好
      setStatusEvents([]);
    }
  }, [quote.id]);

  /**
   * 開票申請按不動的原因。沒有原因就是按得動(設計規格 §3.1)。
   *
   * **刻意不看 `writeBlocked`。** 任何同仁都提得了開票申請(業主 2026-09-15),
   * 財務也提得了 —— 後端那一支守的是 `current_user` 而不是 `require_business`。
   * 拿「報價單對財務唯讀」去擋他,等於把最常要提這件事的人擋在門外。
   *
   * 封存仍然要擋:後端 `ensure_writable()` 會回 409,而對一張已封存的單,
   * 使用者真正的下一步只有「先復原」。
   *
   * 「只有已結案的單提得了」是業主流程裡**唯一一個硬性的解鎖條件** ——
   * 其餘六個狀態一律擋,而 §1.2 說的「狀態隨時改得動」擋的不是這一條:
   * 擋的是「還沒做完就要開發票」這件事本身。
   */
  const invoiceBlockedReason = archived
    ? ARCHIVED_IS_READ_ONLY
    : draft.status !== "CLOSED"
      ? INVOICE_NEEDS_CLOSED
      : undefined;
  // 游標停在哪一個章節。⌘K 從品項庫帶入時要放進「這一章」,而不是猜一個。
  const [activeSection, setActiveSection] = useState<number | null>(null);

  // 換客戶時會蓋掉人工內容,先停在這裡等使用者回答。
  const [pendingClient, setPendingClient] = useState<Company | null>(null);

  /** 把客戶檔的窗口資料寫進乙方那四欄。`skip` 裡的那幾格原封不動。 */
  const fillContactsFrom = (company: Company, skip: AutofillField[] = []) => {
    for (const field of AUTOFILL_FIELDS) {
      if (skip.includes(field)) continue;
      setField(field, company[field] ?? "");
    }
  };

  // 換專案承辦人:甲方的姓名 / 電話 / EMAIL 一起跟著換。
  // 存檔時送出去的只有 owner_user_id,這三欄是後端從那位同仁身上推導的;
  // 這裡先更新畫面,使用者不必存檔才看得到自己選了誰。
  const pickOwner = (user: UserPick | null) => {
    setField("owner_user_id", user?.id ?? null);
    setField("party_a", {
      ...draft.party_a,
      contact_name: user?.name ?? "",
      contact_phone: user?.phone ?? "",
      contact_email: user?.email ?? "",
    });
  };

  // 客戶欄:邊打邊搜尋(業主 2026-11-02,見 components/SearchPicker.tsx)。
  // 舊單子常常只有手打的客戶名稱、沒有客戶檔,那就把那個名字顯示出來,
  // 不要讓它看起來像一張沒有客戶的單。
  const typedClientName = (draft.client_name_override ?? "").trim();
  const currentCompany = companies.find((item) => item.id === draft.client_company_id) ?? null;
  const clientText = draft.client_company_id !== null
    ? currentCompany?.name ?? (draft.party_b.company_name || `客戶 #${draft.client_company_id}`)
    : typedClientName;
  const clientOptions: PickerOption[] = companies.map((company) => ({
    key: String(company.id),
    label: company.name,
    hint: company.tax_id ? `統編 ${company.tax_id}` : undefined,
    match: `${company.name} ${company.tax_id}`,
  }));
  // 新增客戶的對話框:從客戶欄的「新增客戶『…』」打開,名字先帶好。
  const [newClientName, setNewClientName] = useState<string | null>(null);
  const clientActions = (query: string): PickerAction[] => {
    if (query === "") {
      return draft.client_company_id !== null || typedClientName !== ""
        ? [{ key: "clear", label: NO_CLIENT, onSelect: clearClient }]
        : [];
    }
    const exact = companies.some((company) => normalize(company.name) === normalize(query));
    return [
      ...(exact ? [] : [{ key: "typed", label: `只用「${query}」這個名字(不建客戶檔)`, onSelect: () => useTypedClient(query) }]),
      { key: "new", label: `新增客戶「${query}」`, onSelect: () => setNewClientName(query) },
    ];
  };

  // 聯絡人欄:這家客戶的主要聯絡人與所有窗口,點了就帶入四格;也可以直接打一個新的名字
  //(存檔時後端會把它加進這家客戶的窗口,見 app/services/quote_contacts.py)。
  const [clientContacts, setClientContacts] = useState<CompanyContact[]>([]);
  useEffect(() => {
    const companyId = draft.client_company_id;
    setClientContacts([]);
    if (companyId === null) return;
    let alive = true;
    api
      .get<CompanyContact[]>(`/companies/${companyId}/contacts`)
      .then((rows) => {
        if (alive && Array.isArray(rows)) setClientContacts(rows);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [draft.client_company_id]);
  const contactCandidates: { name: string; title: string; mobile: string; phone: string; email: string }[] = [
    ...(currentCompany && currentCompany.contact_name.trim() !== ""
      ? [{ name: currentCompany.contact_name, title: "主要聯絡人", mobile: currentCompany.contact_mobile,
          phone: currentCompany.contact_phone, email: currentCompany.contact_email }]
      : []),
    ...clientContacts
      .filter((c) => normalize(c.name) !== normalize(currentCompany?.contact_name ?? ""))
      .map((c) => ({ name: c.name, title: c.title || c.duty, mobile: c.mobile, phone: c.phone, email: c.email })),
  ];
  const contactOptions: PickerOption[] = contactCandidates.map((c, index) => ({
    key: String(index),
    label: c.name,
    hint: [c.title, c.mobile, c.phone, c.email].filter((x) => x && x.trim() !== "").join(" · ") || undefined,
    match: c.name,
  }));
  const pickContact = (option: PickerOption) => {
    const c = contactCandidates[Number(option.key)];
    if (!c) return;
    setField("contact_name", c.name);
    setField("contact_mobile", c.mobile ?? "");
    setField("contact_phone", c.phone ?? "");
    setField("contact_email", c.email ?? "");
  };

  // 換客戶:乙方的統編與公司地址跟著客戶檔走,窗口那四欄自動帶入
  // (業主:「選好客戶後 自動套入該公司聯絡人/電話/MAIL」)。
  //
  // **已經手動改過的欄位要先問過再覆蓋。** 直接蓋掉會讓人白打一次字,
  // 而且那一下不可逆 —— 他打的內容沒有留在任何地方。客戶本身與統編、地址
  // 照樣立刻換過去:那三個本來就跟著客戶檔走,沒有人在這張單上改過它們。
  /** 不指定客戶:客戶檔與手打的名字都清掉。 */
  function clearClient() {
    setField("client_company_id", null);
    setField("client_name_override", null);
    setField("party_b", { ...draft.party_b, company_name: "", tax_id: "", address: "" });
  }

  /** 客戶檔裡沒有、也不想建檔:只用打的這個名字(舊單「手打客戶名稱」的那一種)。 */
  function useTypedClient(name: string) {
    setField("client_company_id", null);
    setField("client_name_override", name);
    setField("party_b", { ...draft.party_b, company_name: name, tax_id: "", address: "" });
  }

  const pickClient = (option: PickerOption) => {
    const company = companies.find((item) => String(item.id) === option.key);
    if (company) pickCompany(company);
  };

  function pickCompany(company: Company) {
    setField("client_company_id", company.id);
    // 選了檔案裡的客戶就以客戶檔為準,清掉先前手打的簡稱 ——
    // 後端的 client_name_override 會蓋過公司名稱,留著就會變成選了 A、印出來是 B。
    setField("client_name_override", null);
    setField("party_b", {
      ...draft.party_b,
      company_name: company.name,
      tax_id: company.tax_id,
      address: company.address,
    });

    // 會被蓋掉的手打欄位:人打過、現在有字、而且與新客戶的不一樣。
    const clashes = conflictsWith(company);
    if (clashes.length === 0) {
      fillContactsFrom(company);
      return;
    }
    // 先把不衝突的那幾欄帶進來,衝突的停著等使用者回答 ——
    // 要問的只有會被蓋掉的那一格,其餘照樣自動。
    fillContactsFrom(company, clashes);
    setPendingClient(company);
  }

  /**
   * 換成這家客戶時,哪幾格的**人工內容**會被蓋掉。
   *
   * **判定拿現在的值去比,不靠「這次開啟期間有沒有人打過」。** 會話內的記憶
   * 只在同一次開啟的編輯工作階段內成立:業務昨天把聯絡人從客戶檔的「陳小姐」
   * 改成「陳專員(9/14 起代理)」、存檔、離開,隔天回到同一張單(整個編輯器
   * 重新掛載,記憶是空的)再換客戶 —— 那四欄會被靜靜蓋掉,對話框根本不會出現,
   * 而他昨天打的那行字沒有留在任何地方。
   *
   * 所以一格算是人工內容的條件是:有字、**而且**既不等於現在掛著那家客戶的值
   * (那是系統帶進來的,換掉沒有損失),也不等於新客戶的值(蓋了等於沒蓋)。
   * 舊單沒有掛客戶檔時沒有「舊客戶的值」可比,只要有字且與新客戶不同就先問。
   *
   * 前後同值卻真的是手打的那種情形判不出來,但內容一模一樣,蓋掉不會少任何東西。
   */
  function conflictsWith(company: Company): AutofillField[] {
    const current = companies.find((item) => item.id === draft.client_company_id) ?? null;
    return AUTOFILL_FIELDS.filter((field) => {
      const value = draft[field].trim();
      if (value === "") return false;
      if (value === (company[field] ?? "").trim()) return false;
      if (current !== null && value === (current[field] ?? "").trim()) return false;
      return true;
    });
  }

  // 換稅制。含稅的稅率固定 0,切回未稅就把法定的 5% 帶回來 ——
  // 留著剛剛被歸零的 0,使用者一個不注意就寄出一張 0% 的報價單。
  //
  // 顯示文字與草稿裡的小數**一起設定**,不繞道 draft.tax_rate 的同步 effect:
  // 稅率欄先被清空(或打成 abc)時,草稿裡早就是 "0.0000" 了,切到含稅再寫一次
  // 還是 "0.0000" —— 依賴值沒變,effect 根本不會觸發,欄位就這樣空著、唯讀著、
  // 掛著錯誤訊息、鎖著儲存鈕,而使用者打不進那一格。
  const pickTaxMode = (mode: TaxMode) => {
    const percent = mode === "INCLUSIVE" ? ZERO_TAX_PERCENT : DEFAULT_TAX_PERCENT;
    setField("tax_mode", mode);
    setTaxPercent(percent);
    setField("tax_rate", percentToRate(percent));
  };

  // 稅率欄:畫面收百分比,草稿存小數。換算走 decimal.js,不經過 parseFloat / Number。
  const changeTaxPercent = (percent: string) => {
    setTaxPercent(percent);
    setField("tax_rate", percentToRate(percent));
  };

  const save = async () => {
    setSaving(true);
    try {
      const saved = await api.put<QuoteDetail>(`/quotes/${draft.id}`, toQuoteUpdate(draft));
      // 後端重算過的金額與新的 version 一起回來,整份換掉才不會拿舊 version 再送一次。
      replace(saved);
      onSaved?.(saved);
      toast.push("已儲存", "success");
    } catch (error) {
      if (error instanceof ConflictError) {
        setConflict(true);
      } else {
        toast.push(error instanceof Error ? error.message : "儲存失敗", "error");
      }
    } finally {
      setSaving(false);
    }
  };

  /**
   * 從封存區撈回來(`POST /quotes/{id}/restore-from-archive`)。
   *
   * **不碰草稿。** 復原只清掉 `archived_at` 與 `archived_by_user_id`,單號、狀態、
   * 金額、版本史一個字都不動,也不進版(規格 §5)—— 拿回傳值整份取代草稿的話,
   * 使用者在唯讀期間打的字會被無聲洗掉,而他根本還沒存檔。
   *
   * 端點名稱刻意不叫 `/restore`:那一支是「把內容退回三版之前」,兩支都叫復原,
   * 名字撞在一起遲早有人叫錯。
   */
  /**
   * 封存(業主 2026-10-16:報價單裡面最上方的按鈕列也要有)。只有已結案、已取消、草稿封存得了;
   * 有沒存的修改先擋 —— 封存之後就唯讀,那些字再也存不進去。封存完去封存區那一類。
   */
  const [archiving, setArchiving] = useState(false);
  const archiveBlockedReason = !canArchive(draft.status)
    ? ARCHIVE_REFUSED
    : dirty || paymentTermsDirty
      ? "有沒存的修改,請先儲存再封存"
      : undefined;
  const archiveThis = async () => {
    setArchiving(true);
    try {
      const fresh = await api.post<QuoteDetail & QuoteArchiveStamp>(`/quotes/${draft.id}/archive`);
      setArchive({ at: fresh.archived_at ?? null, by: fresh.archived_by_name ?? "" });
      onSaved?.(fresh);
      toast.push(`${draft.quote_no} 已封存`, "success");
      const url = archiveTabUrl(draft.status);
      if (onArchived) onArchived(url);
      else if (typeof window !== "undefined") window.location.assign(`/quote${url}`);
    } catch (error) {
      toast.push(error instanceof Error ? error.message : "封存失敗", "error");
    } finally {
      setArchiving(false);
    }
  };

  const unarchive = async () => {
    setUnarchiving(true);
    try {
      const fresh = await api.post<QuoteDetail & QuoteArchiveStamp>(
        `/quotes/${draft.id}/restore-from-archive`,
      );
      setArchive({ at: fresh.archived_at ?? null, by: fresh.archived_by_name ?? "" });
      onSaved?.(fresh);
      toast.push("已復原,這張報價單又可以編輯了", "success");
    } catch (error) {
      toast.push(error instanceof Error ? error.message : "復原失敗", "error");
    } finally {
      setUnarchiving(false);
    }
  };

  const openStatus = () => {
    setStatusError("");
    setStatusOpen(true);
    void loadStatusEvents();
  };

  /**
   * 人工改狀態(設計規格 §1.2)。**七個值之間任何一種轉換都放行,系統不擋。**
   *
   * 業主第一天就要「更高的自由度」,而案子的實際順序常常不照理論走 ——
   * 先口頭答應就發包、做到一半才補簽、結案之後又追加。硬性的狀態機會在這些
   * 時候擋路,而擋路的系統使用者只會繞過它(一路點到底再點回來),那比沒有
   * 規則更糟。**不擋不代表不記**:每一次都會寫進歷程,就在下面那張清單上。
   *
   * 唯一擋得住的是「有開票申請時退不出已結案」(§3.4),而**那一條由後端擋**:
   * 前端不重寫一份判斷,只把後端回的那一句原樣顯示 —— 訊息裡說得出有幾筆申請,
   * 使用者才知道自己要去撤幾筆。
   *
   * 接回草稿的方式與回簽那一條相同:有未存檔的修改時**只動 status 那一格**,
   * 整份換掉會把使用者正在打的字無聲洗掉,而他做的事只是改了一個狀態。
   */
  const changeStatus = async (next: QuoteStatus) => {
    // 沒有轉換就沒有歷程(後端同樣不留;第十輪「狀態設成它本來就是的值不該
    // 發通知」的延伸)。這裡先擋下來,連一次往返都不必花。
    if (next === draft.status) return;
    // 寫不得的單一個位元組都不送。下拉本身已經反灰,這一行守的是「反灰是畫面,
    // 不是保證」:哪天有人把那個 readOnly 改成傳不進去的 disabled,擋著的就只剩
    // 後端那個 403,而使用者會先看到狀態好像改掉了。
    if (writeBlocked) return;
    setStatusError("");
    setChangingStatus(true);
    try {
      const fresh = await api.post<QuoteDetail & QuoteArchiveStamp>(`/quotes/${draft.id}/status`, {
        status: next,
      });
      if (dirty) setField("status", fresh.status);
      else replace(fresh);
      onSaved?.(fresh);
      await loadStatusEvents();
    } catch (error) {
      // **留在對話框裡,不用 toast**:後端那一句帶著下一步要做什麼
      //(「有 2 筆開票申請,要改狀態請先撤銷申請」),飄過去三秒就消失等於沒說過。
      setStatusError(error instanceof Error ? error.message : "狀態改不動");
    } finally {
      setChangingStatus(false);
    }
  };

  // 套用範本是後端對「已存檔的那一份」動手,而且當場 commit 並 bump version。
  // 有未存檔的修改時套下去,回傳的內容會把那些修改整份蓋掉 —— 所以在開對話框前就擋。
  const openTemplate = useCallback(
    (templateId: number | null) => {
      if (dirty) {
        toast.push("有未存檔的修改,請先儲存再套用範本", "error");
        return;
      }
      setPresetTemplateId(templateId);
      setTemplateOpen(true);
    },
    [dirty, toast],
  );

  // 版本紀錄看的是資料庫裡的那一份。有未存檔的修改時,清單上的「目前版本」與
  // 畫面上的內容根本不是同一件事,差異清單也會少掉正在改的那一段 —— 與匯出 /
  // 寄送 / 套用範本一樣,在開啟前就擋下來。
  const openRevisions = () => {
    if (dirty) {
      toast.push("有未存檔的修改,請先儲存再查看版本紀錄", "error");
      return;
    }
    setRevisionsOpen(true);
  };

  // 復原成功。後端已經寫入並產生新的一版,整份換掉才不會拿舊 version 再存一次。
  const handleRestored = (saved: QuoteDetail) => {
    replace(saved);
    onSaved?.(saved);
    toast.push("已復原,並留下一個新版本", "success");
  };

  const handleApplied = (saved: QuoteDetail) => {
    setTemplateOpen(false);
    // 後端已經套好、重算完並 bump 過 version,整份換掉才不會拿舊 version 再存一次。
    replace(saved);
    onSaved?.(saved);
    toast.push("已套用範本", "success");
  };

  // 網址帶進來的套用請求只處理一次,免得對話框關掉又自己彈回來。
  const handledTemplateRequest = useRef<number | null>(null);
  useEffect(() => {
    if (requestedTemplateId == null) {
      handledTemplateRequest.current = null;
      return;
    }
    if (handledTemplateRequest.current === requestedTemplateId) return;
    handledTemplateRequest.current = requestedTemplateId;
    openTemplate(requestedTemplateId);
    onTemplateRequestHandled?.();
  }, [requestedTemplateId, openTemplate, onTemplateRequestHandled]);

  // ⌘K 選到範本時就地套用。沒登記的話指令面板只能把人丟去範本頁,
  // 那就等於「搜到了卻套不了」—— 這正是驗收清單第三節第 2 條在講的事。
  useEffect(() => {
    if (!onRegisterApplyTemplate) return;
    onRegisterApplyTemplate((template) => openTemplate(template.id));
    return () => onRegisterApplyTemplate(null);
  }, [onRegisterApplyTemplate, openTemplate]);

  // ⌘K 選到品項庫項目:帶進游標所在的章節。
  const pickCatalog = useCallback(
    (hit: CatalogHit) => {
      const patch: Partial<QuoteLine> = {
        name: hit.item_name,
        spec: hit.spec,
        unit: hit.unit,
        unit_price: hit.price,
        variant_id: hit.variant_id,
      };
      if (draft.sections.length === 0) {
        // 這張單還沒有章節(剛建好的新單就是這樣):先開一章,動作才有著落。
        addSection();
        addLine(0, patch);
        toast.push(`已新增章節並帶入「${hit.item_name}」`, "success");
        return;
      }
      // 還沒把游標放進任何章節時,放進最後一章 —— 那是編輯時最可能正在長的地方。
      const index =
        activeSection !== null && activeSection < draft.sections.length
          ? activeSection
          : draft.sections.length - 1;
      addLine(index, patch);
      toast.push(`已把「${hit.item_name}」帶入章節「${draft.sections[index].title || "(未命名章節)"}」`, "success");
    },
    [activeSection, addLine, addSection, draft.sections, toast],
  );

  useEffect(() => {
    if (!onRegisterPickCatalog) return;
    onRegisterPickCatalog(pickCatalog);
    return () => onRegisterPickCatalog(null);
  }, [onRegisterPickCatalog, pickCatalog]);

  // 報價分類導覽用的是草稿的 uid,與 SectionCard 的 DOM id(nq-section-{uid})對得上。
  // 發包狀態一起回報,側邊欄的 tag 才會跟著章節卡片上的下拉一起變。
  useEffect(() => {
    onSectionsChange?.(
      draft.sections.map((section) => ({
        id: section.uid,
        code: section.code,
        title: section.title,
        outsourceStatus: section.outsource_status,
      })),
    );
  }, [draft.sections, onSectionsChange]);

  // ⌘K 的「本單明細」。用的同樣是草稿的 uid,與 LineRow 的 DOM id
  // (nq-line-{uid})對得上,面板選到之後才跳得到那一列。
  // 空摘要的列不回報:它在搜尋結果裡只會是一行「(未命名明細)」,幫不上忙。
  useEffect(() => {
    onLinesChange?.(
      draft.sections.flatMap((section) =>
        section.lines
          .filter((line) => line.name.trim() !== "")
          .map((line) => ({
            id: line.uid,
            name: line.name,
            sectionTitle: section.title || "(未命名章節)",
          })),
      ),
    );
  }, [draft.sections, onLinesChange]);

  // 匯出讀的是資料庫裡的那一份。還沒存檔就下載,拿到的是舊內容 ——
  // 這是很容易踩到的坑,所以直接擋下來並說清楚。
  const openExport = (mode: "export" | "print") => {
    if (dirty) {
      toast.push(
        mode === "print" ? "有未存檔的修改,請先儲存再列印" : "有未存檔的修改,請先儲存再匯出",
        "error",
      );
      return;
    }
    setExportMode(mode);
  };

  // 附件是後端現讀資料庫產的,跟匯出是同一份內容。還沒存檔就寄,
  // 客戶收到的 PDF 是舊的 —— 而且信寄出去就收不回來,所以在開對話框前就擋。
  const openSend = () => {
    if (dirty) {
      toast.push("有未存檔的修改,請先儲存再寄送", "error");
      return;
    }
    setSendOpen(true);
  };

  // 寄送成功。狀態由後端決定(草稿才轉成已寄出,已結案的單子補寄不會倒退),
  // 所以重讀一次拿權威值,而不是在前端自己猜。
  const handleSent = async () => {
    setSendOpen(false);
    toast.push("報價單已寄出", "success");
    try {
      const fresh = await api.get<QuoteDetail>(`/quotes/${draft.id}`);
      replace(fresh);
      onSaved?.(fresh);
    } catch {
      // 信已經寄出去了,只是這次重讀沒成功。這裡絕不能改報成失敗 ——
      // 使用者會再寄一次,客戶就收到兩封。只說明徽章還沒跟上。
      toast.push("已寄出,但畫面沒讀到最新狀態,請重新整理", "info");
    }
  };

  /**
   * 下拉上那七個選項。**字樣一律是後端給的**(設計規格 §1.4)。
   *
   * 還沒載回來時只放目前這一個,而且標籤就是識別字本身:那一瞬間畫面上會看到
   * `IN_PRODUCTION` 這種字,但它誠實 —— 前端在這裡自己補一份中文,就等於又養了
   * 一份會漂開的對照表,而漂開的症狀跟這一瞬間長得一模一樣、卻永遠不會自己好。
   */
  const statusChoices =
    statusOptions.length > 0
      ? statusOptions.map((row) => ({ value: row.value, label: row.label }))
      : [{ value: draft.status, label: draft.status }];

  const reload = async () => {
    setReloading(true);
    try {
      const fresh = await api.get<QuoteDetail>(`/quotes/${draft.id}`);
      replace(fresh);
      setConflict(false);
      toast.push("已載入最新版本", "info");
    } catch (error) {
      toast.push(error instanceof Error ? error.message : "重新載入失敗", "error");
    } finally {
      setReloading(false);
    }
  };

  return (
    // 明細列打 @ 要知道是哪一張單的附件(業主 2026-09-25),見 attachmentMention.ts。
    <QuoteIdContext.Provider value={draft.id ?? null}>
    <div className="flex min-w-0 flex-col gap-4">
      <header className="flex flex-col gap-3 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] px-3 py-3 shadow-card">
        {/* 角色唯讀的橫幅(設計規格 §2.1)。
            財務**進得來這一頁而且看得到金額** —— 看不到金額的財務連發票對不對
            得上都查不了;他做不得的是「改」。所以按鈕留在原地反灰,而這一句話
            先在最上面把原因講完:少了它,一張每個欄位都打得動、按存檔卻沒反應的
            單,只會讓人以為系統壞了(全域規矩:權限與狀態一律帶文字,
            不可以只靠反灰或顏色)。 */}
        {readOnly ? (
          <div
            role="status"
            aria-label="權限狀態"
            className="flex flex-wrap items-center gap-2 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface-2)] px-3 py-2"
          >
            <Lock aria-hidden="true" size={16} className="shrink-0 text-[var(--text-3)]" />
            <span className="min-w-0 flex-1 text-sm text-[var(--text)]">
              {`${QUOTE_IS_READ_ONLY_FOR_ROLE}:這張單的內容與金額看得到,但不能修改、套範本或寄送。`}
            </span>
          </div>
        ) : null}

        {/* 封存橫幅。**一句話說完誰在什麼時候封的、以及下一步要做什麼** ——
            不可以只換個底色或只放一顆鎖頭圖示:看得到顏色的人才知道這張單怎麼了,
            其他人對著一張存不起來的單只會以為系統壞了(全域規矩:狀態一律帶文字)。
            旁邊就放復原,說得出口的動作旁邊要有按鈕。 */}
        {archive.at !== null ? (
          <div
            role="status"
            aria-label="封存狀態"
            className="flex flex-wrap items-center gap-2 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface-2)] px-3 py-2"
          >
            <Lock aria-hidden="true" size={16} className="shrink-0 text-[var(--warn)]" />
            <span className="min-w-0 flex-1 text-sm text-[var(--text)]">
              這張報價單已於 {archivedDay(archive.at)} 由{" "}
              {archive.by.trim() === "" ? UNKNOWN_ARCHIVER : archive.by} 封存。要編輯請先復原。
            </span>
            <Button
              size="sm"
              icon={<ArchiveRestore aria-hidden="true" size={16} />}
              // 從封存撈回來也是業務動作(後端同樣是 ``require_business``)。
              // 財務按得動的話,他會以為自己解得開這張單,實際上只收到一則 403。
              disabled={readOnly}
              title={readOnly ? QUOTE_IS_READ_ONLY_FOR_ROLE : undefined}
              loading={unarchiving}
              onClick={() => {
                void unarchive();
              }}
            >
              復原
            </Button>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <span className="tabular text-base font-semibold text-[var(--text)]">{draft.quote_no}</span>
          {/* 單號旁邊不再掛狀態與請款徽章(業主 2026-10-15):狀態看底下的流程條
              (草稿 → 已寄出 → 已結案 → 申請開票 → 已開票,每格有日期)。 */}
          {/* 改狀態與狀態歷程都在那個對話框裡。**按得動的條件刻意比存檔寬** ——
              歷程任何登入者都看得到(財務也是:一個看不到單據來歷的財務,連
              「這張單什麼時候結的案」都查不了,而那正是他判斷該不該開票的依據),
              真正寫不得的是裡面那個下拉,它自己會反灰並說出原因。 */}
          <Button
            size="sm"
            variant="ghost"
            icon={<Workflow aria-hidden="true" size={16} />}
            onClick={openStatus}
          >
            改狀態
          </Button>
          <Button
            size="sm"
            variant="ghost"
            icon={<History aria-hidden="true" size={16} />}
            onClick={openRevisions}
          >
            版本 {draft.version}
          </Button>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {dirty || paymentTermsDirty ? (
              <span className="text-sm text-[var(--warn)]">{unsavedText(dirty, paymentTermsDirty)}</span>
            ) : null}
            <Button
              icon={<LayoutTemplate aria-hidden="true" size={16} />}
              // 套範本是這張單上最大的一次改動:``replace`` 會把整份七大類清光
              // 再重建。已封存的單是唯讀的(規格 §4.1),財務對報價單也是唯讀的
              // (§2.1),後端兩種都擋 —— 這裡擋的是「按了才知道」,
              // 而橫幅上已經寫著原因。
              disabled={writeBlocked}
              title={writeBlockedReason}
              onClick={() => openTemplate(null)}
            >
              {/* 第 20 輪改名(業主:「套用範本按鈕改字為 範本 / 裡面包含
                  套用現有範本(下拉選項)以及儲存成新範本(命名)」)——
                  那個對話框現在是雙向的,只寫「套用」會讓人找不到儲存。 */}
              範本
            </Button>
            {/* 匯出 PDF 與匯出 Excel 合成一顆(業主 2026-09-17)——
                格式與「要哪一份」在對話框裡一起問完。 */}
            <Button
              icon={<Download aria-hidden="true" size={16} />}
              onClick={() => openExport("export")}
            >
              匯出
            </Button>
            <Button
              icon={<Printer aria-hidden="true" size={16} />}
              onClick={() => openExport("print")}
            >
              列印
            </Button>
            {/* 還沒結案時,反灰的旁邊要有字 —— 一顆只會變灰、不說為什麼的鈕,
                使用者只能去問別人(全域規矩:狀態不可只靠顏色)。
                已封存那一種不在這裡重講:頂上的橫幅已經把原因與下一步說完了。 */}
            {invoiceBlockedReason === INVOICE_NEEDS_CLOSED ? (
              <span className="text-sm text-[var(--text-3)]">{INVOICE_NEEDS_CLOSED_SHORT}</span>
            ) : null}
            <Button
              icon={<Receipt aria-hidden="true" size={16} />}
              // 只有已結案的單提得了(設計規格 §3.1)。**財務按得動** ——
              // 開票申請是他的工作,理由見 `invoiceBlockedReason`。
              disabled={invoiceBlockedReason !== undefined}
              title={invoiceBlockedReason}
              onClick={() => setInvoiceOpen(true)}
            >
              開票申請
            </Button>
            <Button
              icon={<Send aria-hidden="true" size={16} />}
              // 寄出去會把草稿的狀態改成「已寄出」,並對負責同仁發一則指向這張單
              // 的通知,而他在列表上根本找不到它;何況信寄出去就收不回來。
              // 匯出那兩顆不擋 —— 純唯讀,封存正是為了「留著以後可能要查」,
              // 而財務要查發票對不對得上,匯出正是他最需要的那一顆。
              disabled={writeBlocked}
              title={writeBlockedReason}
              onClick={openSend}
            >
              寄送
            </Button>
            {/* 封存(2026-10-16)。已封存的單上方有「復原」,這裡就不畫;財務對報價單唯讀,也不畫 */}
            {!archived && !readOnly ? (
              <Button
                icon={<Archive aria-hidden="true" size={16} />}
                disabled={archiveBlockedReason !== undefined}
                title={archiveBlockedReason}
                loading={archiving}
                onClick={() => void archiveThis()}
              >
                封存
              </Button>
            ) : null}
            <Button
              variant="primary"
              icon={<Save aria-hidden="true" size={16} />}
              // 已封存的單是唯讀的(規格 §4.1),財務對報價單也是唯讀的(§2.1)。
              // 後端兩種都擋(409 / 403),這裡擋的是「按了才知道」——
              // 而 title 說得出是哪一種:一顆只會反灰、不說為什麼的存檔鈕,
              // 使用者只能去問別人。
              disabled={writeBlocked || !dirty || rateError !== null}
              title={writeBlockedReason}
              loading={saving}
              onClick={save}
            >
              儲存
            </Button>
          </div>
        </div>

        {/* 財務摘要 + 流程條(業主 2026-09-30,財務整理 1、3)。只是顯示;狀態照舊用「改狀態」。 */}
        <MoneyStrip quoteId={draft.id} status={draft.status} />

        {/* 安靜表頭:欄位平常無框,hover 出底線,聚焦才顯框。
            專案名稱是這份文件的標題,獨佔一整列 —— 二十來個字擠在三分之一欄寬裡會被切掉。
            空欄位一律給 placeholder:無框又無值的話,根本看不出這裡是可以打字的。 */}
        <div className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
          <div className="sm:col-span-2 lg:col-span-3">
            <QuietField
              label="專案名稱"
              value={draft.title}
              placeholder="未填寫專案名稱"
              onChange={(event) => setField("title", event.target.value)}
            />
          </div>
          {/* 專案時間:單日 / 範圍,印出來的字由後端從日期產生(業主 2026-09-30)。
              以前是一格手打的字 + 起訖日兩格,兩者各填各的、常常對不上。 */}
          <EventPeriodField
            start={draft.event_start_date ?? null}
            end={draft.event_end_date ?? null}
            storedText={quote.event_period ?? ""}
            originalStart={quote.event_start_date ?? null}
            originalEnd={quote.event_end_date ?? null}
            disabled={writeBlocked}
            onChange={(start, end) => {
              setField("event_start_date", start);
              setField("event_end_date", end);
            }}
          />
          <QuietField
            label="專案地點"
            value={draft.venue}
            placeholder="未填寫專案地點"
            onChange={(event) => setField("venue", event.target.value)}
          />
          <QuietField
            label="報價日期"
            type="date"
            value={draft.quote_date}
            onChange={(event) => setField("quote_date", event.target.value)}
          />
          <QuietField
            label="報價有效期限"
            type="date"
            value={draft.valid_until ?? ""}
            onChange={(event) => setField("valid_until", event.target.value || null)}
          />
          <QuietField
            as="select"
            label="稅制"
            options={TAX_MODE_OPTIONS}
            value={draft.tax_mode}
            onChange={(event) => pickTaxMode(event.target.value as TaxMode)}
          />
          {/* 業主:「稅率顯示不要0.0500 直接單位是%數」。單位就貼在欄位旁邊,
              而且掛進 aria-describedby,螢幕閱讀器唸完 5 之後也會唸到 %。
              含稅時 readOnly 與 aria-disabled 一起上 —— 反灰不可以只靠顏色,
              而反灰之後裡面那個 0 仍然要讀得清楚。 */}
          <QuietField
            label="稅率"
            numeric
            inputMode="decimal"
            value={ratePercent}
            suffix="%"
            readOnly={rateLocked}
            aria-disabled={rateLocked || undefined}
            hint={rateLocked ? "含稅報價的總額就是小計,稅率固定 0" : undefined}
            error={rateError}
            onChange={(event) => changeTaxPercent(event.target.value)}
          />
        </div>

        {/* 甲方與乙方並列。甲方全部是推導值(只有承辦人可選),
            乙方只有客戶可選、三個聯絡欄可填。 */}
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <PartyBlock title="甲方" hint="我方,取自公司設定與負責同仁">
            <PartyReadOnlyField
              label="本公司名稱"
              value={draft.party_a.company_name}
              placeholder="設定頁尚未填公司名稱"
            />
            <OwnerPicker
              value={draft.owner_user_id}
              currentName={draft.party_a.contact_name}
              onChange={pickOwner}
            />
            <PartyReadOnlyField
              label="承辦人電話"
              value={draft.party_a.contact_phone}
              placeholder="選了承辦人就會帶出來"
            />
            <PartyReadOnlyField
              label="聯絡地址"
              value={draft.party_a.address}
              placeholder="設定頁尚未填公司地址"
            />
            <PartyReadOnlyField
              label="EMAIL"
              value={draft.party_a.contact_email}
              placeholder="選了承辦人就會帶出來"
            />
          </PartyBlock>

          <PartyBlock title="乙方" hint="客戶,統編與地址跟著客戶檔走;聯絡人四格可以直接改,新的聯絡人存檔時會加進客戶的窗口">
            <SearchPicker
              label="客戶"
              text={clientText}
              placeholder="打字搜尋客戶名稱或統編"
              options={clientOptions}
              onPick={pickClient}
              actions={clientActions}
            />
            <PartyReadOnlyField
              label="統一編號"
              value={draft.party_b.tax_id}
              placeholder="選了客戶就會帶出來"
            />
            <SearchPicker
              label="聯絡人"
              text={draft.contact_name}
              placeholder={draft.client_company_id === null ? "未填寫聯絡人" : "選這家的窗口,或直接打名字"}
              options={contactOptions}
              onPick={pickContact}
              onTextChange={(text) => setField("contact_name", text)}
              showAllWhenEmpty
            />
            {/* 手機與市話分成兩格(業主:「如果電話有兩個 手機或者市內」)。
                兩格各自可改,下面那一格是它們併起來印出去的樣子。 */}
            <QuietField
              label="聯絡手機"
              className={EDITABLE_HINT}
              value={draft.contact_mobile}
              placeholder="未填寫手機"
              onChange={(event) => setField("contact_mobile", event.target.value)}
            />
            <QuietField
              label="聯絡電話"
              className={EDITABLE_HINT}
              value={draft.contact_phone}
              placeholder="未填寫市內電話"
              onChange={(event) => setField("contact_phone", event.target.value)}
            />
            <PartyReadOnlyField
              label="報價單顯示的電話"
              value={joinPhones(draft.contact_mobile, draft.contact_phone)}
              placeholder="兩支電話都空著"
            />
            <QuietField
              label="EMAIL"
              type="email"
              className={EDITABLE_HINT}
              value={draft.contact_email}
              placeholder="未填寫 EMAIL"
              onChange={(event) => setField("contact_email", event.target.value)}
            />
            <PartyReadOnlyField
              label="公司地址"
              value={draft.party_b.address}
              placeholder="選了客戶就會帶出來"
            />
          </PartyBlock>
        </div>
      </header>

      {draft.sections.map((section, index) => (
        <SectionCard
          key={section.uid}
          section={section}
          index={index}
          sectionCount={draft.sections.length}
          subtotal={sectionSubtotals[index] ?? "0.00"}
          onChangeSection={(patch) => setSection(index, patch)}
          onMoveUp={() => moveSection(index, index - 1)}
          onMoveDown={() => moveSection(index, index + 1)}
          onRemove={() => removeSection(index)}
          onAddLine={(patch) => addLine(index, patch)}
          onChangeLine={(lineIndex, patch) => setLine(index, lineIndex, patch)}
          onMoveLine={(from, to) => moveLine(index, from, to)}
          onRemoveLine={(lineIndex) => removeLine(index, lineIndex)}
          onActivate={() => setActiveSection(index)}
        />
      ))}

      <div>
        <Button icon={<Plus aria-hidden="true" size={16} />} onClick={() => addSection()}>
          新增章節
        </Button>
      </div>

      {/* 附件。排在總計之前 —— 總計那一塊是 sticky 的,固定停在編輯器的最後。
          已封存就跟著唯讀:附件照樣看得到(封存正是為了「留著以後可能要查」),
          但傳不了也刪不了(設計規格 §3.6)。 */}
      {/* 回簽上傳就在附件區裡(設計規格 §4:沿用第 12 輪的附件機制)。
          上傳回簽不改狀態(「已回簽」2026-10-15 拿掉了)。 */}
      <AttachmentPanel quoteId={draft.id} archived={archived} />

      <div className="grid grid-cols-1 gap-x-6 gap-y-1 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] px-3 py-3 shadow-card sm:grid-cols-2">
        <QuietField
          as="textarea"
          label="備註"
          rows={3}
          value={draft.notes}
          placeholder="未填寫備註"
          className="resize-y"
          onChange={(event) => setField("notes", event.target.value)}
        />
        <QuietField
          as="textarea"
          label="付款與其他條件"
          rows={3}
          value={draft.terms}
          placeholder="未填寫付款與其他條件"
          className="resize-y"
          onChange={(event) => setField("terms", event.target.value)}
        />
      </div>

      {/* 以上是報價內容,以下是這張單的財務:付款期別、收款、成本(業主 2026-10-01:
          「附件應該要放到備註上面,備註下方全部都是這張單的財務了」)。
          會議紀錄與請客戶回簽排在財務之後 —— 整個順序是業主親口排的,
          tests/totals-bar.test.tsx 釘著。 */}
      <h2 className="m-0 mt-2 border-t border-[var(--border)] pt-4 text-base font-semibold text-[var(--text)]">
        這張單的財務
      </h2>

      {/* 付款條件(設計規格 §2)。財務的第一塊 ——
          它是客戶要在報價單上看到的條件(PDF 印在總計下方),不是內部資料。
          總額傳的是**畫面上當下那個即時總計**,不是資料庫裡那一份:兩邊不同的
          時候,各期金額必須跟著使用者眼前的總計走,否則他會以為系統算錯了。
          寫不得的單(已封存或財務)一樣看得到,只是一個字都改不動。 */}
      <PaymentTerms
        quoteId={draft.id}
        total={totals.total}
        terms={paymentTerms}
        readOnly={writeBlocked}
        readOnlyReason={writeBlockedReason}
        onSaved={setPaymentTerms}
        onDirtyChange={setPaymentTermsDirty}
      />

      {/* 收款記在付款期別上(業主 2026-09-30,財務整理)。只列存過的期別;
          **財務也登錄得了** —— 收款本來就是財務的事,不受報價單唯讀影響。 */}
      <Receipts quoteId={draft.id} terms={paymentTerms} />

      {/* 這張單的成本與毛利(第 16 輪)。放在總計上方 ——
          看完金額接著就是「那我們賺多少」,那是同一個問題的兩半。

          **業務也看得到**:看得到成本的人才會去報帳,而沒報的帳會讓毛利虛高。 */}
      <div id="quote-cost">
        <ProjectCost quoteId={draft.id} readOnly={writeBlocked} />
      </div>

      {/* 這個案子的會議紀錄 / 結案報告 / 檢討報告(第 18 輪)。
          **只列標題與日期,不放編輯器** —— 第 5 輪的「安靜編輯」:
          在這裡塞一個十行的內容框,會讓一個本來只是要改單價的人捲三頁
          才找得到那一格。 */}
      <QuoteMeetingNotes quoteId={draft.id} />

      {/* 請客戶回簽(第 14 輪)。連結與臨時密碼只在按下去的那一刻顯示 ——
          兩者都不進寄件紀錄,而寄信目前卡在網域信譽,所以那一格是現在
          唯一走得通的一條路。 */}
      <SignoffInvite quoteId={draft.id} disabled={writeBlocked} />

      <TotalsBar
        totals={totals}
        taxMode={draft.tax_mode}
        onSpecialPriceChange={writeBlocked ? undefined : (value) => setField("special_price", value)}
      />

      {/* 匯出與列印問的是同一組問題(要哪一份),所以共用同一個對話框。 */}
      <ExportDialog
        key={exportMode ?? "closed"}
        open={exportMode !== null}
        quoteId={draft.id}
        quoteNo={draft.quote_no}
        mode={exportMode ?? "export"}
        onClose={() => setExportMode(null)}
      />

      <SendDialog
        open={sendOpen}
        quote={draft}
        onClose={() => setSendOpen(false)}
        onSent={handleSent}
      />

      <RevisionPanel
        open={revisionsOpen}
        quoteId={draft.id}
        currentVersion={draft.version}
        // 版本紀錄照樣看得到(唯讀是改不動,不是看不到),但裡面那三種復原
        // 走的是與存檔完全相同的寫入路徑 —— 已封存、或角色本來就動不了,
        // 一顆都按不動。**兩個理由要各自傳過去**:面板上那一句要說得出是哪一種,
        // 而「這張單已封存,請先撈回來」對財務是一句做不到也沒用的指示。
        archived={archived}
        readOnlyReason={readOnly ? QUOTE_IS_READ_ONLY_FOR_ROLE : undefined}
        onClose={() => setRevisionsOpen(false)}
        onRestored={handleRestored}
      />

      {/* 開票申請(設計規格 §3)。總額傳下去是為了「沒拆期別的單就開整張單的
          金額」;期別與既有的申請由對話框自己撈,開著才撈 —— 每打開一張單就
          先白撈兩支,只為了一顆多數時候按不動的鈕,不划算。 */}
      <InvoiceRequestDialog
        open={invoiceOpen}
        quoteId={draft.id}
        total={draft.total}
        onClose={() => setInvoiceOpen(false)}
      />

      {/* 狀態與歷程(設計規格 §1)。下拉與歷程放在同一個對話框裡:改完當場就
          看得到自己那一步排在哪 —— 兩個分開的入口會讓「誰在什麼時候改的」
          變成一件要另外想起來去查的事。 */}
      <Dialog
        open={statusOpen}
        onClose={() => setStatusOpen(false)}
        title="狀態與歷程"
        maxWidth="max-w-xl"
      >
        {/* **這一格刻意不用 `QuietField`。** 它的 select 把 `disabled` 寫在展開
            props 之後(`disabled={readOnly || undefined}`),傳上去的 `disabled`
            會被原地蓋成 undefined;而它的 select 版型別又不收 `readOnly`,
            兩條路都走不通。一個看起來反灰、按下去卻真的送得出請求的下拉,
            唯一擋著的只剩後端那個 403,而使用者會先以為狀態已經改掉了。 */}
        <div className="flex min-w-0 items-start gap-2 py-0.5">
          <label
            htmlFor={statusFieldId}
            className="mt-1 w-[6.5rem] shrink-0 whitespace-nowrap text-[13px] leading-5 text-[var(--text-3)]"
          >
            狀態
          </label>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <select
              id={statusFieldId}
              aria-describedby={statusHintId}
              className={quietControlClass({ readOnly: writeBlocked })}
              value={draft.status}
              // 改狀態是業務動作(後端是 `require_business`),已封存的單也擋;
              // 送出中一併鎖住,免得連點兩下送出兩次轉換、留下兩列歷程。
              disabled={writeBlocked || changingStatus}
              aria-disabled={writeBlocked || undefined}
              onChange={(event) => {
                void changeStatus(event.target.value as QuoteStatus);
              }}
            >
              {statusChoices.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <span id={statusHintId} className="text-xs text-[var(--text-3)]">
              六個狀態之間隨時改得動,系統不擋;但每一次轉換都會留在下面的歷程裡
            </span>
          </div>
        </div>
        {writeBlocked ? (
          <p className="m-0 mt-2 text-sm text-[var(--text-2)]">
            {`${writeBlockedReason}:狀態與歷程看得到,但改不動。`}
          </p>
        ) : null}
        {statusError === "" ? null : (
          <p role="alert" className="m-0 mt-2 text-sm text-[var(--danger)]">
            {statusError}
          </p>
        )}

        <h3 className="mb-1 mt-4 text-sm font-semibold text-[var(--text)]">狀態歷程</h3>
        {statusEvents.length === 0 ? (
          <p className="m-0 text-sm text-[var(--text-2)]">這張單還沒有改過狀態。</p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {statusEvents.map((event) => (
              <li
                key={event.id}
                className="flex flex-wrap items-center gap-x-2 gap-y-0.5 border-b border-[var(--border)] py-1 text-sm last:border-b-0"
              >
                <span className="text-[var(--text-2)]">{event.from_label}</span>
                <ArrowRight aria-hidden="true" size={14} className="shrink-0 text-[var(--text-3)]" />
                <span className="font-medium text-[var(--text)]">{event.to_label}</span>
                {/* 人離職、帳號被刪(ON DELETE SET NULL)之後這一欄是空字串:
                    那次轉換還在,只是從此說不出是誰改的 —— 不可以印成空白,
                    看起來像漏畫了一格。 */}
                <span className="ml-auto text-[var(--text-2)]">
                  {event.actor_name.trim() === "" ? UNKNOWN_ACTOR : event.actor_name}
                </span>
                <span className="tabular text-[var(--text-3)]">
                  {formatTaipeiDateTime(event.created_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Dialog>

      <ApplyTemplateDialog
        open={templateOpen}
        quoteId={draft.id}
        sectionCount={draft.sections.length}
        presetTemplateId={presetTemplateId}
        onClose={() => setTemplateOpen(false)}
        onApplied={handleApplied}
      />

      <Dialog
        open={conflict}
        onClose={() => setConflict(false)}
        title="版本衝突"
        closeOnBackdrop={false}
        footer={
          <>
            <Button onClick={() => setConflict(false)}>取消</Button>
            <Button variant="primary" loading={reloading} onClick={reload}>
              重新載入
            </Button>
          </>
        }
      >
        <p className="m-0 text-sm text-[var(--text)]">
          這份報價單已被其他人改過。重新載入會失去你未存的修改。
        </p>
      </Dialog>

      {newClientName !== null ? (
        <CompanyDialog
          open
          initialName={newClientName}
          onClose={() => setNewClientName(null)}
          onSaved={(company) => {
            setCompanies((prev) => [...prev.filter((c) => c.id !== company.id), company]);
            setNewClientName(null);
            pickCompany(company);
          }}
        />
      ) : null}

      {/* 換客戶會蓋掉手打的窗口欄位時,先問過再覆蓋。
          直接蓋掉會讓人白打一次字,而且那一下不可逆 —— 他打的內容沒有留在
          任何地方。哪幾格會被蓋、蓋成什麼,都要講出來,不能只問「確定嗎」。 */}
      <Dialog
        open={pendingClient !== null}
        onClose={() => setPendingClient(null)}
        title="要用新客戶的窗口資料覆蓋嗎?"
        closeOnBackdrop={false}
        footer={
          <>
            <Button onClick={() => setPendingClient(null)}>保留我填的</Button>
            <Button
              variant="primary"
              onClick={() => {
                if (pendingClient !== null) fillContactsFrom(pendingClient);
                setPendingClient(null);
              }}
            >
              覆蓋
            </Button>
          </>
        }
      >
        <p className="m-0 text-sm text-[var(--text)]">
          以下欄位是你自己填的,「{pendingClient?.name}」的窗口資料與它們不一樣:
        </p>
        <ul className="mb-0 mt-2 list-disc pl-5 text-sm text-[var(--text-2)]">
          {pendingClient === null
            ? null
            : conflictsWith(pendingClient).map((field) => (
                <li key={field}>
                  {AUTOFILL_LABEL[field]}:{draft[field] || "(空白)"} →{" "}
                  {pendingClient[field] || "(空白)"}
                </li>
              ))}
        </ul>
      </Dialog>
    </div>
    </QuoteIdContext.Provider>
  );
}
